import { memo, useState, useEffect } from "react";
import { useForm, yupResolver } from "@mantine/form";
import {
  CreatePrivateSessionOverrideInput,
  GetPrivateSessionQuery,
  useCreatePrivateSessionOverrideMutation,
  useUpdatePrivateSessionOverrideMutation,
  useDeletePrivateSessionOverrideMutation,
  useGetPrivateSessionOverrideLazyQuery,
  useGetEmployeesNameLocQuery,
  GetEmployeesNameLocQuery,
} from "src/generated/graphql";
import { useModals } from "@mantine/modals";
import FormCard from "src/components/FormCard";
import { Button, Group, useMantineTheme, Flex } from "@mantine/core";
import { number, object, string, TestContext } from "yup";
import TextInput from "src/components/TextInput/TextInput";
import Textarea from "src/components/Textarea/Textarea";
import Select, { ISelect } from "src/components/Select";
import Switch from "src/components/Switch/Switch";
import NumberInput from "src/components/NumberInput/NumberInput";
import MultiSelect from "src/components/MultiSelect";
import { GetPrivateSession } from "src/api/graphql/queries/PrivateSession";
import { useDisclosure } from "@mantine/hooks";
import { MESSAGES } from "src/utils/constants";
import { useAppContext } from "src/api/AppContext";

type IFormProps = Omit<
  CreatePrivateSessionOverrideInput,
  "employee" | "locations"
> & {
  employee: string;
  locations: string[];
};

type ISessionData = GetPrivateSessionQuery["privateSessionOne"];

interface InstructorDetailProps {
  sessionData: ISessionData;
  handleModal?: (id: string) => void;
  overrideId?: string | undefined;
  disabled?: boolean;
  title?: string;
  visibleCards: string[];
  setvisibleCards: (e: string[]) => void;
  currentEmployee?: ISelect;
  isAllowWrite: boolean;
  isLocationSession: boolean;
}

const InstructorDetail = ({
  sessionData,
  overrideId,
  title,
  visibleCards,
  setvisibleCards,
  currentEmployee,
  isAllowWrite,
  isLocationSession,
}: InstructorDetailProps) => {
  const theme = useMantineTheme();
  const modals = useModals();
  const { currentOrganization, currentLocation: selectedLocation } =
    useAppContext();

  const [instructors, setinstructors] = useState<ISelect[]>([]);
  const [locationsData, setlocationsData] = useState<ISelect[]>([]);
  const [filteredEmployees, setfilteredEmployees] = useState<
    GetEmployeesNameLocQuery["employeeMany"]
  >([]);
  const stepSize = sessionData?.intervalDuration;
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(
    !!overrideId
  );

  const isVisible = overrideId && visibleCards.includes(overrideId);
  const { data: employeesList } = useGetEmployeesNameLocQuery({
    variables: {
      orgId: currentOrganization?.id,
    },
    onCompleted(data) {
      const filteredEmployeess = data.employeeMany.filter((employee) => {
        let currentRooms = sessionData?.rooms;
        if (isLocationSession) {
          currentRooms = sessionData?.rooms.filter((location) => {
            return location?.location.id === selectedLocation?.id;
          });
        }
        return currentRooms?.some((room) => {
          return employee.locations.some((location) => {
            return location.id === room.location.id;
          });
        });
      });

      setfilteredEmployees(filteredEmployeess);
    },
  });

  const refetchQuery = [
    {
      query: GetPrivateSession,
      variables: { id: sessionData?.id! },
    },
  ];

  const [getOveride, { data: overrideDetail }] =
    useGetPrivateSessionOverrideLazyQuery({
      onCompleted() {
        getSetValues();
      },
      fetchPolicy: "network-only",
    });

  const getSetValues = () => {
    const {
      locations,
      commission,
      employee,
      isPercent,
      session,
      description,
      maxDuration,
      minDuration,
      name,
    } = overrideDetail?.privateSessionOverrideOne || {};
    let _data = {
      locations: locations?.map((e) => e.id) || [],
      commission: commission || Number(),
      employee: employee?.id!,
      isPercent: isPercent || false,
      session: { id: session?.id! },
      description,
      maxDuration: maxDuration || undefined,
      minDuration: minDuration || undefined,
      name,
    };
    handleEmpChange(employee?.id!);

    form.setValues(_data);
    form.resetDirty(_data);
  };

  useEffect(() => {
    const filteredEmployeesList: ISelect[] = [];
    const existingEmployees = sessionData?.overrides;

    filteredEmployees?.forEach((val) => {
      const alreadyExists = existingEmployees?.findIndex(
        (e) => e?.employee?.id === val?.id
      );
      if (alreadyExists === -1) {
        const employeeObj = {
          value: val.id,
          label: `${val.user.firstName} ${val.user.lastName}`,
        };
        filteredEmployeesList.push(employeeObj);
      }
    });

    setinstructors([...filteredEmployeesList]);
  }, [filteredEmployees, sessionData?.overrides]);

  const [createOveride, { loading: createLoading }] =
    useCreatePrivateSessionOverrideMutation({
      onCompleted: () => {
        modals.closeAll();
      },
      refetchQueries: refetchQuery,
    });

  const [updateOveride, { loading: updateLoading }] =
    useUpdatePrivateSessionOverrideMutation({
      onCompleted: (data) => {
        if (overrideId) {
          toggleFormActive();
          setvisibleCards(
            visibleCards.filter(
              (e) => e !== data.updatePrivateSessionOverride.id
            )
          );
          form.resetDirty();
        } else {
          modals.closeAll();
        }
      },
      refetchQueries: refetchQuery,
    });
  const [deleteOveride, { loading: deleteLoading }] =
    useDeletePrivateSessionOverrideMutation({
      refetchQueries: refetchQuery,
    });

  const form = useForm<IFormProps>({
    initialValues: {
      name: sessionData?.name,
      description: sessionData?.description,
      minDuration: sessionData?.minDuration,
      maxDuration: sessionData?.maxDuration,
      commission: Number(),
      isPercent: false,
      session: { id: sessionData?.id! },
      locations: [],
      employee: "",
    },
    validate: yupResolver(
      object().shape({
        name: string()
          .trim("No spaces allowed")
          .strict(true)
          .min(2, "Name should have at least 2 letters")
          .max(30, "Name must not be greater than 30 letters")
          .required("Name is required"),
        description: string().max(400).notRequired(),
        employee: string().required("Instructor is required"),
        minDuration: number()
          .test(
            "is-zero",
            "Value must be greater than 0",
            (value) => value !== 0
          )
          .test(
            "is-less-than-max",
            "Must be less than or equal to Max duration",
            function (this: TestContext, value?: number) {
              if (
                !value ||
                value <= (this.parent.maxDuration || sessionData?.maxDuration)
              )
                return true;
              return false;
            }
          ),
        maxDuration: number()
          .test(
            "is-zero",
            "Value must be greater than 0",
            (value) => value !== 0
          )
          .test(
            "is-greater-than-min",
            "Must be greater than or equal to Min duration",
            function (this: TestContext, value?: number) {
              if (
                !value ||
                value >= (this.parent.minDuration || sessionData?.minDuration)
              )
                return true;
              return false;
            }
          ),
        commission: string().required("Commission is required"),
      })
    ),
  });

  const handleDelete = () => {
    deleteOveride({
      variables: { id: overrideId! },
    });
  };

  const handleSubmit = async (values: IFormProps) => {
    const { employee, locations, ...rest } = values;
    let obj = {
      employee: { id: employee },
      locations: locations.map((e) => ({ id: e })),
      ...rest,
    };
    overrideId
      ? updateOveride({
          variables: {
            data: obj,
            id: overrideId,
          },
        })
      : createOveride({
          variables: {
            data: obj,
          },
        });
  };

  const handleEdit = () => {
    toggleFormActive();
    setCardVisible();
  };

  const setCardVisible = () => {
    if (!isVisible && overrideId) {
      getOveride({
        variables: { id: overrideId },
      });
      setvisibleCards([...visibleCards, overrideId]);
    }
  };

  const handleEmpChange = (id: string) => {
    form.setFieldValue("employee", id);
    setlocationsData(
      (employeesList?.employeeMany.find((e) => e.id === id)?.locations || [])
        .filter((location) => {
          if (isLocationSession) {
            return selectedLocation?.id === location.id;
          } else {
            return sessionData?.rooms.some((room) => {
              return room.location.id === location.id;
            });
          }
        })
        .map((val) => ({
          value: val.id,
          label: val.name,
        })) || []
    );
  };

  const handleClick = () => {
    if (isVisible) {
      if (!isFormActive) {
        getSetValues();
        toggleFormActive();
      }
      setvisibleCards(visibleCards.filter((e) => e !== overrideId));
    } else {
      setCardVisible();
    }
  };

  const handleIntervalChange = (num?: number) => {
    const { minDuration, maxDuration } = form.values;
    const _num = num || stepSize || 1;
    if (minDuration && minDuration % _num) {
      form.setFieldValue("minDuration", Math.ceil(minDuration / _num) * _num);
    }
    if (maxDuration && maxDuration % _num) {
      form.setFieldValue("maxDuration", Math.ceil(maxDuration / _num) * _num);
    }
  };

  const isFormUpdated = form.isDirty() && form.isTouched();

  return (
    <>
      <FormCard
        title={title || ""}
        bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
        loading={updateLoading || deleteLoading}
        onDelete={isAllowWrite ? handleDelete : undefined}
        onCancel={handleClick}
        onEdit={isAllowWrite ? handleEdit : undefined}
        onSave={form.onSubmit((values) => handleSubmit(values))}
        hideCard={!overrideId}
        mb={10}
        size="md"
        radius={4}
        isFormActive={isFormActive}
      >
        {(isVisible || !overrideId) && (
          <>
            <Select
              withAsterisk
              data={
                currentEmployee
                  ? [...instructors, currentEmployee]
                  : instructors
              }
              disabled={isFormActive}
              label="Instructor"
              {...form.getInputProps("employee")}
              styles={{ root: { width: "100%" } }}
              onChange={handleEmpChange}
            />

            <Flex justify={"space-between"} align={"center"}>
              <NumberInput
                withAsterisk
                label="Commission"
                maxLength={7}
                precision={2}
                {...form.getInputProps("commission")}
                disabled={isFormActive}
              />

              <Switch
                labelPosition="left"
                label={"Percentage"}
                checked={form.getInputProps("isPercent")?.value}
                {...form.getInputProps("isPercent")}
                mt={"md"}
              />
            </Flex>

            <NumberInput
              label="Min Duration"
              {...form.getInputProps("minDuration")}
              disabled={isFormActive}
              maxLength={7}
              step={stepSize}
              stepHoldDelay={300}
              stepHoldInterval={100}
              onBlur={() => handleIntervalChange()}
            />

            <NumberInput
              label="Max Duration"
              maxLength={7}
              {...form.getInputProps("maxDuration")}
              disabled={isFormActive}
              step={stepSize}
              stepHoldDelay={300}
              stepHoldInterval={100}
              min={stepSize}
              onBlur={() => handleIntervalChange()}
            />

            <MultiSelect
              disabled={isFormActive}
              data={locationsData}
              label="Select Locations"
              {...form.getInputProps("locations")}
            />

            <TextInput
              withAsterisk
              label="Name"
              {...form.getInputProps("name")}
              disabled={isFormActive}
            />
            <Textarea
              label="Description"
              {...form.getInputProps("description")}
              disabled={isFormActive}
            />

            {!overrideId && (
              <Group position="apart" mt="md" style={{ marginTop: 30 }}>
                <Button
                  radius="md"
                  variant="outline"
                  onClick={() => modals.closeAll()}
                  style={{
                    flex: 1,
                    color: theme.colors.secondaryTextColor[6],
                  }}
                  sx={{
                    backgroundColor: theme.colors.secondary[6],
                    "&:hover": {
                      background: theme.colors.secondary[5],
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  radius="md"
                  type="submit"
                  disabled={createLoading || updateLoading}
                  style={{
                    flex: 1,
                  }}
                  sx={{
                    color: theme.colors.primaryTextColor[6],
                    "&:hover": {
                      backgroundColor: theme.colors.primary[4],
                    },
                  }}
                  loading={createLoading || updateLoading}
                >
                  {overrideId ? "Save" : "Create"}
                </Button>
              </Group>
            )}
          </>
        )}
      </FormCard>
    </>
  );
};

export default memo(InstructorDetail);
